import React, { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { AlertTriangle, ChevronLeft, CreditCard, FileText, Plus, Receipt } from "lucide-react";
import { useTranslation } from "react-i18next";
import { TableRowsSkeleton } from "../components/skeleton/Skeleton";
import { useAppDispatch, useAppSelector } from "../app/hooks";
import LeftMenu from "../components/LeftMenu";
import Breadcrumbs from "../components/Breadcrumbs";
import ContractForm from "../components/billing/ContractForm";
import FormModal from "../components/billing/FormModal";
import PaymentForm from "../components/billing/PaymentForm";
import PaymentStatusBadge from "../components/billing/PaymentStatusBadge";
import {
  selectBillingError,
  selectMemberBalance,
} from "../features/billing/billing-slice";
import { fetchMemberBalance } from "../features/billing/slice-extensions/billing-slice-extension";
import { fetchContracts } from "../features/contracts/slice-extensions/contracts-slice-extension";
import { fetchPaymentTypes } from "../features/payment-types/slice-extensions/payment-types-slice-extension";
import { ContractType } from "../features/contracts/models/ContractType";
import { ContractStatus } from "../features/contracts/models/ContractStatus";
import { formatMoney } from "../features/billing/utils/money";
import {
  contractTypeLabel,
  paymentShapeLabel,
} from "../features/payments/utils/paymentLabels";
import { ROUTES } from "../routes";
import { getDisplayLocale } from "../features/utils/timezoneUtils";
import { formatMediumDate } from "../features/utils/displayFormat";

/**
 * Per-Member billing view: their Contracts (with quick payment creation), the
 * latest Payments with statuses, and low-balance pack warnings.
 */
const MemberBillingPage: React.FC = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const dispatch = useAppDispatch();
  const [searchParams] = useSearchParams();
  const balance = useAppSelector(selectMemberBalance(id ?? ""));
  const error = useAppSelector(selectBillingError);
  const [form, setForm] = useState<{ contractId?: string } | null>(
    searchParams.get("newPayment") != null ? {} : null
  );
  const [showContractForm, setShowContractForm] = useState(false);

  useEffect(() => {
    if (!id) return;
    dispatch(fetchMemberBalance(id));
    // The payment form needs the contract list + price catalog ready.
    dispatch(fetchContracts({ page: 1, pageSize: 100 }));
    dispatch(fetchPaymentTypes({ includeArchived: false }));
  }, [id, dispatch]);

  const contractStatusChip = (status: ContractStatus) =>
    status === ContractStatus.Active
      ? "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300"
      : "bg-grey-95 text-grey-30 dark:bg-primary-20 dark:text-grey-70";

  return (
    <section>
      <div className="flex h-full gap-4">
        <LeftMenu />
        <div className="w-full p-4 pb-10 lg:px-10">
          <div className="lg:max-w-[1280px] 2xl:max-w-[1760px]">
            <Breadcrumbs />

            <div className="space-y-4 py-6 lg:space-y-6">
              {/* Header */}
              <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                <h1>
                  {balance
                    ? t("billing.memberPage.title", {
                        name: `${balance.memberFirstName} ${balance.memberLastName}`,
                      })
                    : t("billing.memberPage.fallbackTitle")}
                </h1>
                {/* shrink-0 + nowrap: the title wraps, the buttons never do. */}
                <div className="flex shrink-0 flex-row items-center gap-3">
                  {id && (
                    <Link
                      to={ROUTES.MEMBER_DETAILS.replace(":id", id)}
                      className="btn btn-secondary whitespace-nowrap"
                    >
                      <ChevronLeft size={18} strokeWidth={2} />
                      <span>{t("billing.memberPage.backToMember")}</span>
                    </Link>
                  )}
                  <button
                    type="button"
                    className="btn btn-primary whitespace-nowrap"
                    onClick={() => setForm({})}
                  >
                    <Plus size={18} strokeWidth={2} />
                    <span>{t("billing.actions.newPayment")}</span>
                  </button>
                </div>
              </div>

              {!balance && !error && (
                <TableRowsSkeleton rows={4} />
              )}
              {error && (
                <p className="text-sm text-red-600 dark:text-red-400">
                  {error.message ?? t("billing.memberPage.errorLoad")}
                </p>
              )}

              {balance && (
                <div className="grid grid-cols-1 items-start gap-4 lg:gap-6 xl:grid-cols-2">
                  {/* Contracts */}
                  <div className="with-border rounded-2xl p-4 lg:p-6">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <div className="flex items-center gap-2">
                        <FileText size={20} className="text-grey-30 dark:text-grey-70" />
                        <h3>{t("billing.memberPage.contracts")}</h3>
                      </div>
                      <button
                        type="button"
                        className="btn btn-text"
                        onClick={() => setShowContractForm(true)}
                      >
                        <Plus size={16} strokeWidth={2} />
                        <span>{t("billing.actions.newContract")}</span>
                      </button>
                    </div>
                    {balance.contracts.length === 0 ? (
                      <p className="mt-3 text-sm text-grey-40 dark:text-grey-60">
                        {t("billing.memberSection.noContracts")}
                      </p>
                    ) : (
                      <div className="mt-4 space-y-2">
                        {balance.contracts.map((c) => (
                          <div
                            key={c.id}
                            className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-grey-90 px-3 py-2 dark:border-primary-20"
                          >
                            <div className="text-sm">
                              <p className="font-medium text-grey-10 dark:text-grey-90">
                                {contractTypeLabel(c.type, t)}
                                {c.type === ContractType.VisitPack && c.packSize
                                  ? ` · ${t("billing.memberPage.packOf", { size: c.packSize })}`
                                  : ""}
                                {c.type !== ContractType.VisitPack &&
                                c.monthlyFeeAmountMinorUnits != null && c.monthlyFeeCurrency
                                  ? ` · ${t("billing.common.perMonth", { amount: formatMoney(c.monthlyFeeAmountMinorUnits, c.monthlyFeeCurrency, getDisplayLocale()) })}`
                                  : ""}
                              </p>
                              <p className="mt-0.5 flex flex-wrap items-center gap-2 text-xs text-grey-30 dark:text-grey-70">
                                <span className={`rounded-full px-2 py-0.5 font-medium ${contractStatusChip(c.status)}`}>
                                  {t(`billing.contractStatus.${c.status}`, { defaultValue: c.status })}
                                </span>
                                <span>{t("billing.common.since", { date: formatMediumDate(`${c.validFrom.slice(0, 10)}T00:00:00`,getDisplayLocale()) })}</span>
                                {c.cadence && <span>· {c.cadence}</span>}
                              </p>
                            </div>
                            {c.status === ContractStatus.Active && (
                              <button
                                type="button"
                                className="btn btn-text"
                                onClick={() => setForm({ contractId: c.id })}
                              >
                                <CreditCard size={16} />
                                <span>{t("billing.actions.addPayment")}</span>
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Recent payments */}
                  <div className="with-border rounded-2xl p-4 lg:p-6">
                    <div className="flex items-center gap-2">
                      <Receipt size={20} className="text-grey-30 dark:text-grey-70" />
                      <h3>{t("billing.memberPage.recentPayments")}</h3>
                    </div>
                    {balance.recentPayments.length === 0 ? (
                      <p className="mt-3 text-sm text-grey-40 dark:text-grey-60">{t("billing.memberPage.noPayments")}</p>
                    ) : (
                      <div className="mt-4 space-y-2">
                        {balance.recentPayments.map((p) => (
                          <div
                            key={p.id}
                            className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-grey-90 px-3 py-2 dark:border-primary-20"
                          >
                            <div className="text-sm">
                              <p className="font-medium text-grey-10 dark:text-grey-90">
                                {formatMoney(p.amountMinorUnits, p.amountCurrency, getDisplayLocale())}
                                <span className="ml-2 font-normal text-grey-30 dark:text-grey-70">
                                  {paymentShapeLabel(p.shape, t)}
                                </span>
                              </p>
                              <p className="mt-0.5 text-xs text-grey-30 dark:text-grey-70">
                                {formatMediumDate(p.dateCreated, getDisplayLocale())}
                                {p.remainingBalance === undefined
                                  ? ""
                                  : p.remainingBalance === 0
                                    ? ` · ${t("billing.memberPage.packUsedUp")}`
                                    : ` · ${t("billing.memberPage.visitsLeft", { count: p.remainingBalance })}`}
                              </p>
                            </div>
                            <span className="flex items-center gap-1">
                              {balance.lowBalancePackIds.includes(p.id) && (
                                <span className="inline-flex items-center gap-1 rounded-full bg-secondary-95 px-2 py-0.5 text-xs font-medium text-secondary-30 dark:bg-secondary-20 dark:text-secondary-80">
                                  <AlertTriangle size={11} />
                                  {t("billing.common.lowBalance")}
                                </span>
                              )}
                              <PaymentStatusBadge status={p.status} />
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {showContractForm && id && (
                <FormModal onClose={() => setShowContractForm(false)}>
                  <ContractForm
                    initialMemberId={id}
                    onSaved={() => {
                      setShowContractForm(false);
                      dispatch(fetchMemberBalance(id));
                      dispatch(fetchContracts({ page: 1, pageSize: 100 }));
                    }}
                    onClose={() => setShowContractForm(false)}
                  />
                </FormModal>
              )}

              {form && id && (
                <FormModal onClose={() => setForm(null)}>
                  <PaymentForm
                    mode="standalone"
                    initialMemberId={id}
                    initialContractId={form.contractId}
                    onSaved={() => { setForm(null); dispatch(fetchMemberBalance(id)); }}
                    onClose={() => setForm(null)}
                  />
                </FormModal>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default MemberBillingPage;
